import { Popover } from "@headlessui/react";
import { EllipsisVerticalIcon, FunnelIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { FeatureFlags } from "@utils/FeatureFlags/generated-flags";
import { GoalReviewTypes } from "@utils/constants";
import { orgEhrUnusableMessage } from "@utils/ehrActions";
import { isReportGoalVisible } from "@utils/reportVisibility";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import { Small, Tooltip } from "@fronterahealth/frontera-ui-components";

import { ReportGoalType, useGetReportDomainsQuery } from "@api/graphql/types-and-hooks";
import { CaptureProgressIndicator } from "@components/ChartCapture/CaptureProgressIndicator";
import { OffscreenChartCaptureRunner } from "@components/ChartCapture/OffscreenChartCaptureRunner";
import { EHRLoginDialog } from "@components/ClinicalNewReportDialog/EHRLoginDialog";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { useEhrAnalytics } from "@hooks/useEhrAnalytics";
import { ReportType, useReportAnalytics } from "@hooks/useReportAnalytics";
import {
  ApproveAllAttestation,
  ApproveAllAttestationDialog,
} from "@pages/ClinicalReportDetails/ReportPage/Components/ApproveAllAttestationDialog";
import { DomainCard } from "@pages/ClinicalReportDetails/ReportPage/Components/DomainCard";
import { GoalsFilterPopover } from "@pages/ClinicalReportDetails/ReportPage/Components/GoalsFilterPopover";
import { GoalsSyncButton } from "@pages/ClinicalReportDetails/ReportPage/Components/GoalsSyncButton";
import { ManageDomainsDialog } from "@pages/ClinicalReportDetails/ReportPage/Components/ManageDomains/ManageDomainsDialog";
import { ReorderGoalsModal } from "@pages/ClinicalReportDetails/ReportPage/Components/ReorderGoalsModal";
import { useApproveAllUnreviewed } from "@pages/ClinicalReportDetails/ReportPage/hooks/useApproveAllUnreviewed";
import { useManualRefreshProgramming } from "@pages/ClinicalReportDetails/ReportPage/hooks/useManualRefreshProgramming";
import { getGoalsGroupedByDomain } from "@pages/ClinicalReportDetails/ReportPage/utils/sortGoalsByDomain";
import { useAdminData } from "@providers/AdminDataProvider";
import { useClinicalReportDetailsData } from "@providers/ClinicalReportDetailsProvider";
import { useReportProgrammingData } from "@providers/ReportProgrammingProvider";
import { useReport } from "@providers/ReportProvider";

interface GoalsSidebarProps {
  reportId: string;
  goalList: ReportGoalType[];
  onSearch?: () => void;
}

export const GoalsSidebar: React.FC<GoalsSidebarProps> = ({ reportId, goalList, onSearch }) => {
  const [showFilters, setShowFilters] = useState(false);
  const [isExpandedAll, setIsExpandedAll] = useState<boolean>(true);
  const [isReorderModalOpen, setIsReorderModalOpen] = useState(false);
  const [isManageDomainsOpen, setIsManageDomainsOpen] = useState(false);
  const [isApproveAllConfirmOpen, setIsApproveAllConfirmOpen] = useState(false);
  const prevExpandedAllWhenUnapprovedRef = useRef<boolean>(true);
  const {
    refresh: refreshProgramming,
    isSyncing: isRefreshingProgramming,
    orgEhrUnusable,
    ehrConnectionStatus,
    noCredentials,
    isEhrSyncCleared,
  } = useManualRefreshProgramming();
  const [ehrLoginDialogOpen, setEhrLoginDialogOpen] = useState(false);
  const { selected, filters, setFilters, dispatchSelected } = useReportProgrammingData();

  // Bulk approval for BCBAs (incl. assessment-bcba) and admins, gated on the LaunchDarkly flag. Only
  // renders on a reassessment that is still editable — initial assessments
  // don't have the per-goal/target approval surface, and a completed
  // report's items can't be re-approved. Checks `roles` directly rather than
  // `isUserBCBA` because the latter explicitly excludes admins, and we want
  // admins able to bulk-approve here too.
  const flags = useFlags<FeatureFlags>();
  const { roles } = useAdminData();
  const { isReassessment, isReportCompleted, isInitialAssessment } = useClinicalReportDetailsData();
  const { report } = useReport();
  let reportType: ReportType = "diagnosis_builder";
  if (isReassessment) reportType = "reassessment";
  else if (isInitialAssessment) reportType = "initial_assessment";
  const { captureGoalSidebarSelect, captureApproveAll } = useReportAnalytics({
    report_id: reportId,
    report_type: reportType,
    template_id: report?.reportTemplate?.id,
    template_name: report?.reportTemplate?.name,
    template_payor_type: report?.reportTemplate?.payorType ?? undefined,
    template_export_framework: report?.reportTemplate?.exportFramework ?? undefined,
  });
  const {
    approveAll,
    isApplying,
    unapprovedGoalCount,
    unapprovedTargetCount,
    captureJobs,
    captureItems,
    onItemStart,
    onItemCaptured,
  } = useApproveAllUnreviewed(reportId);
  const canApprove = roles.includes("bcba") || roles.includes("assessment-bcba") || roles.includes("admin");
  const showApproveAll = flags["approve-all-goals"] && canApprove && isReassessment && !isReportCompleted;
  const hasUnreviewedItems = unapprovedGoalCount + unapprovedTargetCount > 0;
  const isFronteraInternal = roles.includes("frontera-internal");

  const { data: reportDomainsData } = useGetReportDomainsQuery({ reportId }, { enabled: !!reportId });

  const showUnapprovedOnly = filters.reviewed.includes(GoalReviewTypes["Not Approved"]);

  useEffect(() => {
    if (!showUnapprovedOnly) {
      setIsExpandedAll(prevExpandedAllWhenUnapprovedRef.current);
    }
  }, [showUnapprovedOnly]);

  const { captureEhrRefreshClicked } = useEhrAnalytics();
  const handleRefreshProgramming = useCallback(() => {
    // Managed-onboarding gate: an uncleared tenant must not reach the EHR — the
    // noCredentials branch opens the login dialog (credential entry = the first
    // EHR touch), so guard here before either branch. The menu item is also
    // disabled below; the sibling GoalsSyncButton carries the inline notice.
    if (!isEhrSyncCleared) return;
    captureEhrRefreshClicked("report_goals_step");
    if (noCredentials) setEhrLoginDialogOpen(true);
    else refreshProgramming();
  }, [isEhrSyncCleared, noCredentials, refreshProgramming, captureEhrRefreshClicked]);

  const setExpandedAllForPopover = useCallback(
    (expanded: boolean) => {
      if (expanded) {
        prevExpandedAllWhenUnapprovedRef.current = isExpandedAll;
      }
      setIsExpandedAll(expanded);
    },
    [isExpandedAll],
  );

  const domainGroups = useMemo(
    () => getGoalsGroupedByDomain(goalList, reportDomainsData?.getReportDomains ?? []),
    [reportDomainsData, goalList],
  );

  // Goals are numbered continuously across the whole plan (1..N) using only the
  // goals that appear in the report (isReportGoalVisible), so the sidebar numbers
  // match the review and export. Report-excluded goals still render, but without
  // a number. Map: goal id → 0-based report index.
  const goalNumberById = useMemo(() => {
    const map = new Map<string, number>();
    let index = 0;
    for (const group of domainGroups) {
      for (const goal of group.goals) {
        if (isReportGoalVisible(goal)) {
          map.set(goal.id, index);
          index += 1;
        }
      }
    }
    return map;
  }, [domainGroups]);

  const activeFilterCount =
    [filters.status, filters.reviewed].filter((arr) => arr.length > 0).length +
    (filters.dateRange.preset !== null ? 1 : 0);

  const actionButtonMenu: { label: string; onClick: () => void; disabled?: boolean; tooltip?: string }[] = [
    {
      label: "Reorder goals",
      onClick: () => setIsReorderModalOpen(true),
    },
    {
      label: "Expand/Collapse all",
      onClick: () => setIsExpandedAll((prev) => !prev),
    },
    {
      label: "Refresh programming",
      onClick: handleRefreshProgramming,
      // Org-admin EHR connection isn't usable — a refresh would log in with the
      // same failed org credentials, so disable rather than offer a doomed sync.
      // Also disabled while the tenant isn't cleared for EHR sync (managed
      // onboarding); the sibling GoalsSyncButton renders the inline explanation.
      disabled: orgEhrUnusable || !isEhrSyncCleared,
      tooltip: orgEhrUnusable ? orgEhrUnusableMessage(ehrConnectionStatus) : undefined,
    },
    ...(showApproveAll
      ? [
          {
            label: "Approve all goals/targets",
            onClick: () => setIsApproveAllConfirmOpen(true),
            disabled: !hasUnreviewedItems || isApplying,
          },
        ]
      : []),
    ...(isFronteraInternal
      ? [
          {
            label: "Manage domains",
            onClick: () => setIsManageDomainsOpen(true),
          },
        ]
      : []),
  ];

  const runApproveAll = async (attestation: ApproveAllAttestation) => {
    const goalCount = unapprovedGoalCount;
    const targetCount = unapprovedTargetCount;
    try {
      const { successes, failures } = await approveAll(attestation);
      if (successes > 0) {
        captureApproveAll(goalCount, targetCount, successes);
        notifySuccess(`Approved ${successes} item${successes === 1 ? "" : "s"}`);
      }
      if (failures > 0) {
        notifyError(`Failed to approve ${failures} item${failures === 1 ? "" : "s"}`);
      }
    } catch {
      notifyError("Failed to approve goals.");
    } finally {
      setIsApproveAllConfirmOpen(false);
    }
  };

  const isNoGoals = goalList.length === 0;

  const selectedGoalId = selected?.mode !== "none" ? selected.goalId : undefined;
  const selectedTargetId = selected?.mode === "target" ? selected.targetId : undefined;

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between border-b px-2 min-h-12">
        <div className="relative flex items-center gap-1">
          <button className="rounded-sm p-[3px]" onClick={onSearch}>
            <MagnifyingGlassIcon className="h-[18px] w-[18px] text-icon-secondary" />
          </button>
          <button className="relative rounded-sm p-[3px]" onClick={() => setShowFilters((prev) => !prev)}>
            <FunnelIcon
              className={twMerge(
                "h-[18px] w-[18px]",
                activeFilterCount > 0 ? "text-icon-primary" : "text-icon-secondary",
              )}
            />
            {activeFilterCount > 0 && (
              <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-green-900 text-[10px] font-semibold leading-3 text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
          {showFilters && (
            <GoalsFilterPopover
              filters={filters}
              onFiltersChange={setFilters}
              onClose={() => setShowFilters(false)}
              setExpandedAll={setExpandedAllForPopover}
            />
          )}
        </div>
        <div className="flex items-center gap-1">
          <GoalsSyncButton />
          {/* Built from primitives (not <PopoverMenu />) so the trigger and
              icon visually match the per-goal ellipsis menus in GoalLineRow —
              same outline-variant icon, stroke weight, and hit area. */}
          <Popover className="relative">
            {({ close }) => (
              <>
                <Popover.Button className="flex items-center justify-center w-8 h-6 rounded-sm hover:bg-limestone-200 transition-colors focus:outline-hidden">
                  <EllipsisVerticalIcon className="w-4 h-4 text-icon-secondary" strokeWidth={2} />
                </Popover.Button>
                {/* `w-max` so the panel grows to fit the longest label
                    (e.g. "Approve all goals/targets") while still snapping
                    its right edge to the trigger. `min-w-56` floors the
                    width when only the shorter default items render. */}
                <Popover.Panel className="absolute right-0 z-10 mt-1 w-max min-w-56 rounded-lg bg-white shadow-lg ring-1 ring-black/5 focus:outline-hidden">
                  <div className="py-1">
                    {actionButtonMenu.map((item) => {
                      const menuButton = (
                        <button
                          type="button"
                          disabled={item.disabled}
                          className={twMerge(
                            "block w-full whitespace-nowrap px-4 py-2 text-left text-sm text-text-primary hover:bg-limestone-50",
                            item.disabled && "cursor-not-allowed text-text-tertiary hover:bg-transparent",
                          )}
                          onClick={() => {
                            if (item.disabled) return;
                            item.onClick();
                            close();
                          }}
                        >
                          {item.label}
                        </button>
                      );
                      // A disabled button doesn't emit hover events, so the
                      // Tooltip wraps it to explain why the item is unavailable.
                      return item.tooltip ? (
                        <Tooltip
                          key={item.label}
                          text={item.tooltip}
                          placement="left"
                          showInfoIcon={false}
                          className="block"
                        >
                          {menuButton}
                        </Tooltip>
                      ) : (
                        <React.Fragment key={item.label}>{menuButton}</React.Fragment>
                      );
                    })}
                  </div>
                </Popover.Panel>
              </>
            )}
          </Popover>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isNoGoals && <Small className="px-3 py-3 text-text-tertiary">No goals found</Small>}

        {!isNoGoals &&
          domainGroups.map((group) => (
            <DomainCard
              key={group.domainId}
              domain={group.domain}
              goalNumberById={goalNumberById}
              domainName={group.domainName}
              goals={group.goals}
              selectedGoalId={selectedGoalId}
              selectedTargetId={selectedTargetId}
              onGoalClick={(goal) => {
                dispatchSelected({ type: "SELECT_GOAL", goalId: goal.id });
                captureGoalSidebarSelect(goal.id, goal.reportDomain?.id);
              }}
              onTargetClick={(target, goal) =>
                dispatchSelected({ type: "SELECT_TARGET", targetId: target.id, goalId: goal.id })
              }
              expandAll={isExpandedAll}
              showUnapprovedOnly={showUnapprovedOnly}
              isRefreshingProgramming={isRefreshingProgramming}
            />
          ))}
      </div>

      <ReorderGoalsModal
        open={isReorderModalOpen}
        onClose={() => setIsReorderModalOpen(false)}
        reportId={reportId}
        goalList={goalList}
      />

      {isFronteraInternal && (
        <ManageDomainsDialog
          open={isManageDomainsOpen}
          onClose={() => setIsManageDomainsOpen(false)}
          reportId={reportId}
          goalList={goalList}
        />
      )}

      <ApproveAllAttestationDialog
        open={isApproveAllConfirmOpen}
        isApplying={isApplying}
        progressContent={<CaptureProgressIndicator items={captureItems} />}
        onCancel={() => setIsApproveAllConfirmOpen(false)}
        onConfirm={runApproveAll}
      />

      {captureJobs && (
        <OffscreenChartCaptureRunner jobs={captureJobs} onItemStart={onItemStart} onItemCaptured={onItemCaptured} />
      )}

      {ehrLoginDialogOpen && (
        <EHRLoginDialog
          open={ehrLoginDialogOpen}
          setOpen={setEhrLoginDialogOpen}
          setSuccessLogin={(success) => {
            setEhrLoginDialogOpen(false);
            if (success) refreshProgramming();
          }}
        />
      )}
    </div>
  );
};
